import { useState, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import { useDateTimeHelpers } from '../hooks/useDateTimeHelpers';

export const useReservationForm = (modelData, resource) => {
    const { t } = useTranslation();
    const { createDateObject, createDateObjectFromUtc } = useDateTimeHelpers();

    const [formData, setFormData] = useState({
        reserved_from: '',
        reserved_until: '',
        reservation_notes: '',
        dynamic_fields: {}
    });
    const [reservedFromDate, setReservedFromDate] = useState(null);
    const [reservedUntilDate, setReservedUntilDate] = useState(null);
    const [internalLoading, setInternalLoading] = useState(false);
    const [error, setError] = useState('');

    // Initialize form data
    useEffect(() => {
        if (modelData) {
            // Normalize dynamic_fields: extract values if they come in the enriched {value, definition} format
            let normalizedDynamicFields = {};
            if (modelData.dynamic_fields) {
                Object.entries(modelData.dynamic_fields).forEach(([key, fieldData]) => {
                    // Recursively unwrap enriched format {value, definition}
                    let val = fieldData;
                    while (val && typeof val === 'object' && 'value' in val && 'definition' in val) {
                        val = val.value;
                    }
                    normalizedDynamicFields[key] = val;
                });
            }

            setFormData({
                reserved_from: modelData.reserved_from || '',
                reserved_until: modelData.reserved_until || '',
                reservation_notes: modelData.reservation_notes || modelData.notes || '',
                dynamic_fields: normalizedDynamicFields
            });

            if (modelData.reserved_from) {
                setReservedFromDate(createDateObjectFromUtc(modelData.reserved_from));
            }
            if (modelData.reserved_until) {
                setReservedUntilDate(createDateObjectFromUtc(modelData.reserved_until));
            }
        } else {
            // Set default to 1 hour from now
            const now = new Date();
            const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

            setFormData({
                reserved_from: now.toISOString(),
                reserved_until: oneHourLater.toISOString(),
                reservation_notes: '',
                dynamic_fields: {}
            });

            setReservedFromDate(createDateObject(now.toISOString()));
            setReservedUntilDate(createDateObject(oneHourLater.toISOString()));
        }
        setError('');
    }, [modelData, createDateObject, createDateObjectFromUtc]);

    const handleReservedFromDateChange = (date) => {
        setReservedFromDate(date);
        setFormData(prev => ({
            ...prev,
            reserved_from: date ? date.toDate().toISOString() : ''
        }));
    };

    const handleReservedUntilDateChange = (date) => {
        setReservedUntilDate(date);
        setFormData(prev => ({
            ...prev,
            reserved_until: date ? date.toDate().toISOString() : ''
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDynamicFieldsChange = (fields) => {
        setFormData(prev => ({
            ...prev,
            dynamic_fields: fields
        }));
    };

    const validateDates = () => {
        if (!formData.reserved_from || !formData.reserved_until) {
            return t('reservation.dates_required');
        }

        const fromDate = new Date(formData.reserved_from);
        const untilDate = new Date(formData.reserved_until);
        const now = new Date();

        if (fromDate < now) {
            return t('reservation.from_date_past');
        }

        if (untilDate <= fromDate) {
            return t('reservation.until_date_invalid');
        }

        return null;
    };

    const handleSubmit = async (e, onSubmit) => {
        e.preventDefault();
        setInternalLoading(true);
        setError('');

        const dateError = validateDates();
        if (dateError) {
            setError(dateError);
            setInternalLoading(false);
            return;
        }

        try {
            const submitData = {
                reserved_from: formData.reserved_from,
                reserved_until: formData.reserved_until,
                reservation_notes: formData.reservation_notes || null,
                borrowed_at: null,
                dynamic_fields: formData.dynamic_fields || {},
                ...(resource && { resource_id: resource.id })
            };

            if (onSubmit) {
                await onSubmit(submitData);
            }
        } catch (err) {
            console.error('Failed to submit reservation form:', err);
            setError(err.response?.data?.detail || t('common.error_occurred'));
            throw err;
        } finally {
            setInternalLoading(false);
        }
    };

    return {
        formData,
        reservedFromDate,
        reservedUntilDate,
        internalLoading,
        error,
        handleReservedFromDateChange,
        handleReservedUntilDateChange,
        handleInputChange,
        handleDynamicFieldsChange,
        handleSubmit
    };
};
